/* ===========================
 * Provider Profile Transformer
 * ===========================
 *
 * The transformer for the provider profile
 */

import Transformer from './transformer';
import store from '../store';
import providerTypes from '../utils/providerTypes';
import stringUtil, { toEnThObject } from '../utils/stringUtil';
import userTransformer from './user';
import { isNotEmpty } from '../utils/arrayUtil';

export default class ProviderProfileTransformer extends Transformer {
  /**
   * Method used to transform a fetched profile
   *
   * @param profile The fetched profile
   *
   * @returns {Object} The transformed profile
   */
  static fetch(profile) {
    const employeeCode = Array.isArray(profile.employee_code) ? profile.employee_code : [];
    const fallbackEmployeeCode = [];
    const typeMapping = {
      app: ['app', 'online'],
      clinic: ['clinic', 'offline'],
    };
    if (!employeeCode.length) {
      if (profile.online_code_name) {
        fallbackEmployeeCode.push({
          code_name: profile.online_code_name,
          type: 'app',
        });
      }
      if (profile.offline_code_name) {
        fallbackEmployeeCode.push({
          code_name: profile.offline_code_name,
          type: 'clinic',
        });
      }
    }
    const normalizedEmployeeCode = employeeCode.length
      ? employeeCode.map((item) => ({
          id: item.id,
          code_name: item.code_name,
          type: item.type,
        }))
      : fallbackEmployeeCode;
    const onlineMatch = normalizedEmployeeCode.find((item) => typeMapping.app.includes(item.type));
    const onlineCodeName = (onlineMatch && onlineMatch.code_name) || profile.online_code_name || '';
    const offlineMatch = normalizedEmployeeCode.find((item) => typeMapping.clinic.includes(item.type));
    const offlineCodeName = (offlineMatch && offlineMatch.code_name) || profile.offline_code_name || '';

    return {
      id: profile.id,
      user_id: profile.user_id,
      prefix: profile.prefix,
      firstname: profile.firstname,
      lastname: profile.lastname,
      employee_code: normalizedEmployeeCode,
      online_code_name: onlineCodeName,
      offline_code_name: offlineCodeName,
      description: profile.description.th,
      description_en: profile.description.en,
      slug: profile.slug,
      citizenid: profile.citizenid,
      passport: profile.passport,
      gender: profile.gender,
      language: profile.language,
      type: profile.type,
      visibility: profile.visibility ? profile.visibility : [],
      call_channel: profile.call_channel ? profile.call_channel : [],
      hidden: profile.hidden,
      tel: profile.user ? profile.user.tel : '',
      license_number: profile.license_number,
      expertises: profile.expertises ? profile.expertises : [],
      address: profile.address,
      workplace: profile.workplace,
      education: profile.education,
      certificate: profile.certificate,
      opening: profile.opening,
      dayoff: profile.dayoff,
      approved: profile.approved,
      topics: profile.topics ? profile.topics : [],
      avatar: profile.avatar,
      point: profile.point,
      announcement: profile.announcement,
      price: profile.price,
      price_gain: profile.price_gain,
      price_service: profile.price_service,
      price_donation: profile.price_donation,
      donation_id: profile.donation_id,
      current_performance: profile.current_performance,
      last_active: profile.last_active,
      total_income: profile.total_income,
      wallet: profile.wallet,
      bank_account: profile.bank_account,
      bank_brand: profile.bank_brand,
      bank_branch: profile.bank_branch,
      fullName: this.fullName(profile),
      fullNameTH: this.fullName(profile, 'th'),
      user: profile.user ? userTransformer.fetch(profile.user) : {},
      tags: profile.tags ? profile.tags : [],
      provider_types: profile.provider_types ? this.fetchProviderType(profile.provider_types) : {},
      provider_sub_types: profile.provider_sub_types ? this.fetchProviderSubtype(profile.provider_sub_types) : {},
      video_link: profile.video_link,
      level: profile.level || false,
      trainings: profile.trainings ? profile.trainings.map((e) => ({ text: e.th, textEn: e.en, year: e.year })) : [],
      supported_tests: profile.supported_tests ? profile.supported_tests.map((e) => e.th) : [],
      supported_services: profile.supported_services ? profile.supported_services.map((e) => e.th) : [],
      supported_therapies: profile.supported_therapies ? profile.supported_therapies.map((e) => e.th) : [],
      supported_age_ranges:
        profile.supported_age_ranges && profile.supported_age_ranges.length > 0
          ? `${profile.supported_age_ranges[0].min} - ${profile.supported_age_ranges[0].max}`
          : null,
      experiences: profile.experiences
        ? profile.experiences.map((e) => ({ text: e.th, textEn: e.en, year: e.year }))
        : [],
      awards: profile.awards ? profile.awards.map((e) => ({ text: e.th, textEn: e.en, year: e.year })) : [],
      quotes:
        profile.quotes && profile.quotes.length > 0
          ? [{ en: profile.quotes[0].en || '', th: profile.quotes[0].th || '' }]
          : [{ en: '', th: '' }],
      links: profile.links ? profile.links.map((e) => ({ text: e.url })) : [],
      supported_patient_groups: profile.supported_patient_groups
        ? profile.supported_patient_groups.map((e) => e.th)
        : [],
      is_available_at_clinic: profile.is_available_at_clinic,
      provider_tier_id: profile.provider_tier_id || null,
    };
  }

  static fetchProviderSubtype(providersubtypes) {
    const subtypeList = providersubtypes.map((data) => {
      return {
        id: data.id,
        created_at: data.created_at,
        provider_profile_id: data.provider_profile_id,
        provider_sub_type_id: data.provider_sub_type_id,
        provider_types: this.fetchProviderType(data.provider_types),
      };
    });

    return subtypeList;
  }

  static fetchProviderType(providerType) {
    return {
      id: providerType.id,
      name_en: this.getLangName(providerType.name, 'en'),
      name_th: this.getLangName(providerType.name, 'th'),
      description_en: this.getLangName(providerType.description, 'en'),
      description_th: this.getLangName(providerType.description, 'th'),
      parent_id: providerType.parent_id,
      created_at: providerType.created_at,
      updated_at: providerType.updated_at,
    };
  }

  static fetchForTable(profile) {
    return {
      id: profile.id,
      user_id: profile.user_id,
      prefix: profile.prefix,
      firstname: profile.firstname,
      lastname: profile.lastname,
      citizenid: profile.citizenid,
      passport: profile.passport,
      gender: profile.gender,
      language: profile.language,
      type: profile.type,
      visibility: profile.visibility,
      call_channel: profile.call_channel,
      certificate: profile.certificate,
      opening: profile.opening,
      dayoff: profile.dayoff,
      approved: profile.approved,
      avatar: profile.avatar,
      last_active: profile.last_active,
      email: profile.user.email,
      tel: profile.user.tel,
      fullName: this.fullName(profile),
      fullNameTH: this.fullName(profile, 'th'),
      hidden: profile.hidden,
      performance: profile.current_performance || 0,
      allowDonation: profile.allow_donation || false,
      level: profile.level || false,
    };
  }

  static fetchWithAppointments(profile) {
    const fetched = this.fetch(profile);
    fetched.appointments = profile.appointments.data;
    fetched.schedule = profile.schedule ? profile.schedule : null;
    return fetched;
  }

  static fetchCollectionWithAppointments(items) {
    const newCollection = [];

    items.forEach((item) => {
      newCollection.push(this.fetchWithAppointments(item));
    });

    return newCollection;
  }

  static fetchWithPayment(profile) {
    const fetched = this.fetch(profile);
    fetched.slips = profile.slips;
    fetched.total_paid = profile.total_paid / 100;
    fetched.vat_paid = profile.vat_paid / 100;
    fetched.total_paid_with_vat = profile.total_paid_with_vat / 100;
    fetched.total_unpaid = profile.total_unpaid / 100;
    fetched.tax_unpaid = profile.tax_unpaid / 100;
    fetched.total_unpaid_with_tax = profile.total_unpaid_with_tax / 100;
    return fetched;
  }

  static fetchCollectionWithPayment(items) {
    const newCollection = [];

    items.forEach((item) => {
      newCollection.push(this.fetchWithPayment(item));
    });

    return newCollection;
  }

  /**
   * Method used to transform a send profile
   *
   * @param profile The profile to be send
   *
   * @returns {Object} The transformed profile
   */
  static send(profile) {
    const supportedAgeRange = profile.supported_age_ranges
      ? profile.supported_age_ranges.split('-').map((e) => e.trim())
      : [];
    const typeMapping = {
      app: ['app', 'online'],
      clinic: ['clinic', 'offline'],
    };
    const sanitizeCodeName = (value) => (typeof value === 'string' ? value.trim() : value || '');
    const existingEmployeeCode = Array.isArray(profile.employee_code)
      ? profile.employee_code.filter((item) => item).map((item) => ({ ...item }))
      : [];

    const onlineCodeName = sanitizeCodeName(profile.online_code_name);
    const offlineCodeName = sanitizeCodeName(profile.offline_code_name);
    const ensureCanonicalEntry = (types, canonicalType, codeName) => {
      let index = existingEmployeeCode.findIndex((item) => item && types.includes(item.type));
      if (index > -1) {
        // Update existing entry - keep the id if it exists
        existingEmployeeCode[index] = {
          ...existingEmployeeCode[index],
          code_name: codeName,
          type: canonicalType,
        };
      } else if (codeName !== '') {
        // Create new entry without id (API will create new record)
        existingEmployeeCode.push({
          code_name: codeName,
          type: canonicalType,
        });
        index = existingEmployeeCode.length - 1;
      }

      // Remove duplicate entries of the same type
      for (let i = existingEmployeeCode.length - 1; i >= 0; i -= 1) {
        if (i === index) {
          continue;
        }
        const item = existingEmployeeCode[i];
        if (item && types.includes(item.type)) {
          existingEmployeeCode.splice(i, 1);
        }
      }
    };

    ensureCanonicalEntry(typeMapping.app, 'app', onlineCodeName);
    ensureCanonicalEntry(typeMapping.clinic, 'clinic', offlineCodeName);

    const employeeCodePayload = existingEmployeeCode
      .map((item) => {
        if (!item || !item.type) {
          return null;
        }
        const payload = {
          type: item.type,
          code_name: sanitizeCodeName(item.code_name),
        };
        // Include id if it exists (for update), otherwise API will create new record
        if (item.id) {
          payload.id = item.id;
        }
        return payload;
      })
      .filter((item) => item && (item.code_name !== '' || item.id));

    let retval = {
      id: profile.id,
      visibility: profile.visibility,
      call_channel: profile.call_channel,
      hidden: profile.hidden,
      bank_account: profile.bank_account,
      bank_brand: profile.bank_brand,
      bank_branch: profile.bank_branch,
      prefix: profile.prefix,
      firstname: profile.firstname,
      lastname: profile.lastname,
      description: {
        en: profile.description_en,
        th: profile.description,
      },
      slug: profile.slug.toLowerCase(),
      type: profile.type,
      subtypes: profile.provider_sub_types.id ? [profile.provider_sub_types.id] : [],
      expertises: profile.expertises ? profile.expertises.map((exp) => exp.id) : [],
      license_number: profile.license_number,
      expertise: profile.expertise,
      address: profile.address,
      workplace: profile.workplace,
      certificate: profile.certificate,
      approved: profile.approved,
      provider_type_id: profile.provider_types.id,
      video_link: profile.video_link,
      employee_code: employeeCodePayload,
      online_code_name: onlineCodeName,
      offline_code_name: offlineCodeName,
      education: profile.education.map((item) => ({
        country: item.country,
        degree: item.degree,
        curriculum: item.curriculum,
        curriculumEn: item.curriculumEn,
        institute: item.institute,
        instituteEn: item.instituteEn,
        year: Number(item.year),
      })),
      trainings: isNotEmpty(profile.trainings)
        ? profile.trainings.map((item) => ({ text: item.text, textEn: item.textEn, year: item.year }))
        : [],
      links: isNotEmpty(profile.links) ? profile.links.map((item) => ({ text: item.text, textEn: item.textEn })) : [],
      experiences: isNotEmpty(profile.experiences)
        ? profile.experiences.map((item) => ({ text: item.text, textEn: item.textEn, year: item.year }))
        : [],
      awards: isNotEmpty(profile.awards)
        ? profile.awards.map((item) => ({ text: item.text, textEn: item.textEn, year: item.year }))
        : [],
      quotes: isNotEmpty(profile.quotes) ? [{ th: profile.quotes[0].th || '', en: profile.quotes[0].en || '' }] : [],
      supported_age_ranges:
        isNotEmpty(supportedAgeRange) > 0 ? [{ min: supportedAgeRange[0], max: supportedAgeRange[1] }] : [],
      supported_patient_groups: isNotEmpty(profile.supported_patient_groups)
        ? profile.supported_patient_groups.map((e) => toEnThObject(e))
        : [],
      supported_therapies: isNotEmpty(profile.supported_therapies)
        ? profile.supported_therapies.map((e) => toEnThObject(e))
        : [],
      supported_services: isNotEmpty(profile.supported_services)
        ? profile.supported_services.map((e) => toEnThObject(e))
        : [],
      supported_tests: isNotEmpty(profile.supported_tests) ? profile.supported_tests.map((e) => toEnThObject(e)) : [],
      is_available_at_clinic: profile.is_available_at_clinic === true,
    };
    if (profile.tel) {
      retval = Object.assign(retval, {
        tel: profile.tel,
      });
    }
    if (profile.email) {
      retval = Object.assign(retval, {
        email: profile.email,
      });
    }
    if (profile.price) {
      retval = Object.assign(retval, {
        price: profile.price,
      });
    }
    if (profile.provider_tier_id) {
      retval = Object.assign(retval, {
        provider_tier_id: profile.provider_tier_id,
      });
    }
    return retval;
  }

  static sendToggle(profile) {
    let retval = {
      // visibility: profile.visibility,
      // call_channel: profile.call_channel,
      hidden: profile.hidden,
    };
    return retval;
  }

  static updateInfo(profile) {
    const retval = {
      prefix: profile.prefix,
      firstname: profile.firstname,
      lastname: profile.lastname,
      description: profile.description,
      description_en: profile.description_en,
      slug: profile.slug.toLowerCase(),
      citizenid: profile.citizenid,
      passport: profile.passport,
      gender: profile.gender,
      language: profile.language,
      expertise: profile.expertise,
      address: profile.address,
      workplace: profile.workplace,
      education: profile.education,
      topics: profile.selectedTopics.topics,
      new_topics: profile.selectedTopics.new_topics,
    };
    if (profile.timezone) {
      retval.timezone = profile.timezone;
    }
    if (profile.timezone_setting) {
      retval.timezone_setting = profile.timezone_setting;
    }
    return retval;
  }

  static fullName(profile, lang = 'en') {
    // console.log(profile)
    if (!profile) {
      return '';
    }

    if (!profile.firstname) {
      return '';
    }

    // let lang = store.state.lang.language;
    if (!profile.firstname[lang]) {
      lang = 'en';
    }
    let prefix = '';
    if (profile.prefix) {
      prefix = `${profile.prefix[lang]} `;
    }

    return `${prefix + profile.firstname[lang]} ${profile.lastname[lang]}`;
  }

  static shortName(profile) {
    if (!profile) {
      return '';
    }

    if (!profile.firstname) {
      return '';
    }

    let lang = store.state.lang.language;
    if (!profile.firstname[lang]) {
      lang = 'en';
    }
    let prefix = '';
    if (profile.prefix) {
      prefix = `${profile.prefix[lang]} `;
    }

    return `${prefix + profile.firstname[lang]}`;
  }

  static typeName(profile) {
    if (!profile) {
      return '';
    }

    if (!profile.type) {
      return '';
    }
    return stringUtil.capitalize(profile.type);
  }

  static languagesString(profile) {
    return this.languagesArray(profile).join(', ');
  }

  static approvedTopics(profile) {
    if (!profile) {
      return [];
    }

    if (!profile.topics) {
      return [];
    }

    return profile.topics.filter((topic) => topic.approved);
  }

  static scorePassThreshold(profile) {
    const scoreThreshold = 1;
    if (!profile) {
      return false;
    }
    if (!profile.point) {
      return false;
    }
    return profile.point >= scoreThreshold;
  }

  static category(profile) {
    if (profile.type === providerTypes.GENERAL || profile.type === providerTypes.SPECIALIST) {
      return providerTypes.CATEGORY_PHYSICAL;
    }
    return providerTypes.CATEGORY_MENTAL;
  }

  static localizedDescription(profile) {
    const lang = store.state.lang.language;
    if (lang === 'en' && profile.description_en) {
      return profile.description_en;
    }
    return profile.description;
  }

  static userPrice(profile) {
    let price = profile.price;
    if (price === 0) {
      if (store.state.pricing.pricing !== null) {
        price = store.state.pricing.pricing[profile.type].price;
      } else {
        price = -1;
      }
    }
    return price;
  }

  static providerGain(profile) {
    const category = this.category(profile);
    let providerGain = profile.price_gain;
    if (providerGain === null) {
      if (store.state.pricing.pricing !== null) {
        if (category === providerTypes.CATEGORY_MENTAL) {
          providerGain = store.state.pricing.pricing[profile.type].gain.middle + profile.current_performance;
        } else {
          providerGain = store.state.pricing.pricing[profile.type].gain;
        }
      } else {
        providerGain = -1;
      }
    }
    return providerGain;
  }

  static getRankFromGain(gain, type) {
    if (gain >= 0) {
      const priceSettings = store.state.pricing.pricing;
      const middle = priceSettings[type].gain.middle;
      const upper = priceSettings[type].gain.upper;
      if (gain >= upper) {
        return 3;
      }
      if (gain >= middle) {
        return 2;
      }
      return 1;
    }
    return -1;
  }

  static providerGainRank(profile) {
    return this.getRankFromGain(this.providerGain(profile), profile.type);
  }

  static callChannel(profile, prefix = ',') {
    return window._.map(profile.call_channel, window._.capitalize).join(prefix);
  }

  static visibility(profile, prefix = ',') {
    // return window._.capitalize(profile.visibility.join(prefix || ","));
    return window._.map(profile.visibility, window._.capitalize).join(prefix);
  }

  static providerContact(one) {
    let text = '';
    if (one.email) text += `<a href="mailto:${one.email}">${one.email}</a>`;
    if (one.tel) text += `<br>(<a href="tel:${one.tel}">${one.tel}</a>)`;
    return text;
  }

  static getLangName(name, lang) {
    const nameObject = JSON.parse(name);

    return lang === 'en' ? nameObject.en : nameObject.th;
  }
}

export const providerMixin = {
  methods: {
    callChannel: ProviderProfileTransformer.callChannel,
    visibility: ProviderProfileTransformer.visibility,
    providerContact: ProviderProfileTransformer.providerContact,
    providerName: ProviderProfileTransformer.fullName,
    providerType: ProviderProfileTransformer.typeName,
  },
};
